import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ClipboardList, HeartPulse, PiggyBank, Scale, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bar, Disclaimer, Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { FormDialog } from "@/components/ResourceTable";
import { useWsData } from "@/hooks/useWsData";
import { api, brl, brlShort, dateBR, KIND_LABEL } from "@/lib/api";

const COLORS = ["#D4AF37", "#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EC4899", "#EF4444", "#14B8A6"];
const RISK = [{ value: "conservador", label: "Conservador" }, { value: "moderado", label: "Moderado" }, { value: "arrojado", label: "Arrojado" }];

export const PROFILE_FIELDS = [
  { name: "income", label: "Renda / faturamento mensal (R$)", type: "number" },
  { name: "expenses", label: "Despesas mensais (R$)", type: "number" },
  { name: "assets", label: "Total de ativos (R$) — se não cadastrados", type: "number" },
  { name: "cash", label: "Reserva / caixa disponível (R$)", type: "number" },
  { name: "debt", label: "Total de dívidas (R$) — se não cadastradas", type: "number" },
  { name: "debt_apr", label: "Juros médio das dívidas (% a.a.)", type: "number" },
  { name: "risk", label: "Perfil de risco", type: "select", options: RISK, default: "moderado" },
  { name: "objective", label: "Objetivo principal", default: "Organizar as finanças" },
  { name: "age", label: "Idade", type: "number", default: 35 },
  { name: "retire_age", label: "Idade desejada para aposentadoria", type: "number", default: 65 },
  { name: "desired_income", label: "Renda desejada na aposentadoria (R$/mês)", type: "number" },
  { name: "dependents", label: "Dependentes", type: "number" },
];

const BIZ_GOALS = ["Organizar o fluxo de caixa", "Aumentar o faturamento", "Reduzir custos e despesas", "Melhorar a margem de lucro", "Quitar dívidas da empresa", "Formar capital de giro", "Expandir / investir"].map((v) => ({ value: v, label: v }));
export const BIZ_PROFILE_FIELDS = [
  { name: "income", label: "Faturamento mensal médio (R$) — usado só se não houver lançamentos", type: "number", wide: true },
  { name: "expenses", label: "Custos + despesas mensais (R$) — usado só se não houver lançamentos", type: "number", wide: true },
  { name: "assets", label: "Ativos da empresa (R$) — se não cadastrados", type: "number" },
  { name: "cash", label: "Caixa / capital de giro disponível (R$)", type: "number" },
  { name: "debt", label: "Empréstimos e financiamentos (R$) — se não cadastrados", type: "number" },
  { name: "debt_apr", label: "Juros médio das dívidas (% a.a.)", type: "number" },
  { name: "risk", label: "Apetite a risco da empresa", type: "select", options: RISK, default: "moderado" },
  { name: "objective", label: "Objetivo principal da empresa", type: "select", options: BIZ_GOALS, default: BIZ_GOALS[0].value },
];

const Tip = { contentStyle: { background: "#0B1A33", border: "1px solid #1E3A6E", borderRadius: 8 }, formatter: (v) => brl(v) };

function ScoreRing({ score }) {
  const color = score >= 70 ? "#10B981" : score >= 45 ? "#D4AF37" : "#EF4444";
  return (
    <div className="flex items-center gap-5" data-testid="health-score">
      <div className="relative h-24 w-24 rounded-full" style={{ background: `conic-gradient(${color} ${score * 3.6}deg, #0B1A33 0)` }}>
        <div className="absolute inset-2 flex items-center justify-center rounded-full bg-[#0F2347]"><span className="num text-2xl font-semibold">{score}</span></div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400">Score de saúde financeira</div>
        <div className="mt-1 text-lg font-semibold" style={{ color }}>{score >= 70 ? "Saudável" : score >= 45 ? "Atenção" : "Crítico"}</div>
      </div>
    </div>
  );
}

function AllocationLegend({ items }) {
  const total = items.reduce((a, x) => a + x.value, 0) || 1;
  return (
    <ul className="mt-3 space-y-1.5 text-xs" data-testid="allocation-legend">
      {[...items].sort((a, b) => b.value - a.value).map((x) => (
        <li key={x.name} className="flex items-center justify-between gap-2" data-testid="allocation-legend-item">
          <span className="flex min-w-0 items-center gap-2 text-slate-200">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: COLORS[items.indexOf(x) % COLORS.length] }} />
            <span className="truncate">{x.name}</span>
          </span>
          <span className="num shrink-0 text-white">{brl(x.value)} <span className="text-slate-400">· {((x.value / total) * 100).toFixed(1).replace(".", ",")}%</span></span>
        </li>
      ))}
    </ul>
  );
}

function Charts({ d }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Section title="Fluxo de caixa mensal" className="lg:col-span-2" testid="cashflow-chart">
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={d.cash_flow}>
              <defs>
                <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#10B981" stopOpacity={0.4} /><stop offset="1" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                <linearGradient id="gd" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#EF4444" stopOpacity={0.3} /><stop offset="1" stopColor="#EF4444" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="#1E3A6E" strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={brlShort} width={70} />
              <Tooltip {...Tip} />
              <Area dataKey="receitas" name="Receitas" stroke="#10B981" fill="url(#gr)" strokeWidth={2} />
              <Area dataKey="despesas" name="Despesas" stroke="#EF4444" fill="url(#gd)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>
      <Section title="Distribuição do patrimônio" testid="allocation-chart">
        {d.allocation.length ? (
          <>
            <div className="h-48">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={d.allocation} dataKey="value" nameKey="name" innerRadius={45} outerRadius={78} paddingAngle={2}>
                    {d.allocation.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
                  </Pie>
                  <Tooltip {...Tip} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <AllocationLegend items={d.allocation} />
          </>
        ) : <p className="text-sm text-slate-400">Cadastre ativos em <Link className="text-[#F5D77F]" to="/app/patrimonio">Patrimônio</Link>.</p>}
      </Section>
    </div>
  );
}

export default function Dashboard() {
  const { data: d, reload, wid } = useWsData("/dashboard");
  const [open, setOpen] = useState(false);
  if (!d) return <Spinner />;
  const saveProfile = async (p) => {
    await api.put(`/workspaces/${wid}/profile`, p);
    toast.success("Diagnóstico atualizado");
    reload();
  };
  const ro = d.workspace.my_role === "viewer";
  const pj = d.workspace.kind === "empresa";
  const FIELDS = pj ? BIZ_PROFILE_FIELDS : PROFILE_FIELDS;
  const mlabel = d.flow_month ? ` · ${d.flow_month.slice(5)}/${d.flow_month.slice(0, 4)}` : "";
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={KIND_LABEL[d.workspace.kind]} title={`Diagnóstico · ${d.workspace.name}`} subtitle="Visão consolidada do seu fluxo de caixa, patrimônio, riscos e prioridades.">
        {!ro && <Button onClick={() => setOpen(true)} data-testid="edit-profile-btn"><ClipboardList className="mr-1 h-4 w-4" /> {d.has_profile ? "Atualizar diagnóstico" : "Fazer diagnóstico"}</Button>}
      </PageHeader>
      {!d.has_profile && (
        <div className="mb-6 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-sm text-[#F5D77F]" data-testid="profile-missing">
          {pj ? "Os números do mês já vêm dos lançamentos. Falta só clicar em \"Fazer diagnóstico\" e informar caixa disponível, dívidas e objetivo da empresa para gerar o plano e o cronograma de 12 meses. Isso não depende de fechar o mês." : "Responda o diagnóstico financeiro para gerar seu plano personalizado e o cronograma de 12 meses."}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={(pj ? "Faturamento" : "Renda mensal") + mlabel} value={brl(d.income)} tone="green" icon={TrendingUp} testid="kpi-income" />
        <Kpi label={(pj ? "Custos e despesas" : "Despesas mensais") + mlabel} value={brl(d.expenses)} tone="red" icon={TrendingDown} testid="kpi-expenses" delay={50} />
        <Kpi label={(pj ? "Resultado" : "Saldo mensal") + mlabel} value={brl(d.monthly_surplus)} tone={d.monthly_surplus >= 0 ? "green" : "red"} icon={Scale} testid="kpi-surplus" delay={100} />
        <Kpi label="Patrimônio líquido" value={brl(d.net_worth)} tone="gold" icon={Wallet} testid="kpi-networth" delay={150}
          hint={`Ativos ${brlShort(d.assets)} · Dívidas ${brlShort(d.debt)}`} />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section testid="score-card"><ScoreRing score={d.score} />
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Reserva</span><span className="num" data-testid="runway">{d.runway_months.toFixed(1)} meses</span></div>
            <Bar value={(d.runway_months / 6) * 100} color="#8B5CF6" />
            <div className="flex justify-between"><span className="text-slate-400">Cronograma</span><span className="num">{d.tasks_done}/{d.tasks_total} etapas</span></div>
            <Bar value={d.tasks_total ? (d.tasks_done / d.tasks_total) * 100 : 0} />
            <div className="flex justify-between"><span className="text-slate-400">Investimentos</span><span className="num">{brl(d.investments)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Faturas em aberto</span><span className="num">{brl(d.cards_open)}</span></div>
          </div>
        </Section>
        <Section title="Alertas" testid="flags-card">
          {d.flags.length ? d.flags.map((f) => <div key={f} className="mb-3 flex gap-2 text-sm text-amber-200"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />{f}</div>)
            : <p className="text-sm text-emerald-300">Nenhum alerta crítico no momento.</p>}
        </Section>
        <Section title="Prioridades — Método PROSPERA" testid="priorities-card">
          <ol className="space-y-3 text-sm">
            {d.priorities.map((p, i) => <li key={p} className="flex gap-3"><span className="num flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/15 text-xs text-[#F5D77F]">{i + 1}</span>{p}</li>)}
          </ol>
        </Section>
      </div>
      <div className="mt-6"><Charts d={d} /></div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Últimas movimentações" action={<Link to="/app/lancamentos" className="text-xs text-[#F5D77F]" data-testid="see-all-tx">Ver todas</Link>}>
          {d.recent_transactions.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b border-[#1E3A6E]/50 py-2.5 text-sm last:border-0">
              <div><div>{t.description}</div><div className="text-xs text-slate-500">{dateBR(t.date)} · {t.category}</div></div>
              <span className={`num ${t.kind === "receita" ? "text-emerald-400" : "text-red-400"}`}>{t.kind === "receita" ? "+" : "−"}{brl(t.amount)}</span>
            </div>
          ))}
          {!d.recent_transactions.length && <p className="text-sm text-slate-400">Sem movimentações.</p>}
        </Section>
        <Section title="Metas" action={<Link to="/app/metas" className="text-xs text-[#F5D77F]">Gerenciar</Link>}>
          {d.goals.map((g) => (
            <div key={g.id} className="mb-4">
              <div className="mb-1 flex justify-between text-sm"><span>{g.title}</span><span className="num text-slate-400">{Math.round((g.current / g.target) * 100)}%</span></div>
              <Bar value={(g.current / g.target) * 100} color="#D4AF37" />
            </div>
          ))}
          {!d.goals.length && <p className="text-sm text-slate-400"><PiggyBank className="mr-1 inline h-4 w-4" />Crie sua primeira meta.</p>}
        </Section>
      </div>
      <Disclaimer text={d.disclaimer} />
      <FormDialog open={open} onOpenChange={setOpen} title={pj ? "Diagnóstico da empresa" : "Diagnóstico financeiro"} fields={FIELDS} testid="profile-dialog"
        initial={d.has_profile ? Object.fromEntries(FIELDS.map((f) => [f.name, d.profile[f.name] ?? f.default ?? ""])) : null} onSubmit={saveProfile} />
      <HeartPulse className="hidden" />
    </div>
  );
}
